import 'react-loading-skeleton/dist/skeleton.css';

import { startTransition, useContext, useEffect, useRef, useState } from 'react';
import { StoreIdentifierName, TrendingSearch } from 'types';
import {
  aosSelector,
  apiEndpointsSelector,
  breakpointSelector,
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from 'store/selectors/commonSelector';
import { WindowContext } from 'utility/windowContext';
import { headerTaxoSelector } from 'store/selectors/taxonomySelector';
import { useAppSelector } from 'store/store';
import ErrorBoundaryWithLogger from 'common/ErrorBoundary/ErrorBoundary';
import MainLogo from 'common/MainLogo/MainLogo';
import MyStore, { MyStoreFallback } from '../MyStore/MyStore';
import SameDayDelivery from '../SameDayDelivery/SameDayDelivery';
import CartCount from '../Cart/Cart';
import HamburgerMenu from '../ProgressiveHeader/HamburgerMenu/HamburgerMenu';
import HeaderLogo from '../HeaderLogo/HeaderLogo';
import MobileMenuToggleButton from '../MobileMenuToggleButton/MobileMenuToggleButton';
import MyAccount from '../MyAccount/MyAccount';
import SearchBar from '../SearchBar/SearchBar';
import TopMessage from '../NavBar/TopMessage';
import SAYTModalMobile from '../SearchBar/SAYTModalMobile/SAYTModalMobile';
import './MainHeader.scss';

declare global {
  interface Window {
    authFunctions: any;
    ceopsStatus: any;
  }
}

export interface MainHeaderProps {
  switchState: () => void;
  myAccountDrawerSwitchState: () => void;
  trendingSearchData: TrendingSearch | undefined;
  showMobileMenu: boolean;
  setShowMobileMenu: (value: boolean) => void;
  hasTopNavViewEventBeenCalled: boolean;
  setHasTopNavViewEventBeenCalled: (value: boolean) => void;
  showTrendingSearch: boolean;
}

const MainHeader = ({
  switchState,
  myAccountDrawerSwitchState,
  trendingSearchData,
  showMobileMenu,
  setShowMobileMenu,
  hasTopNavViewEventBeenCalled,
  setHasTopNavViewEventBeenCalled,
  showTrendingSearch,
}: MainHeaderProps) => {
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const taxoContent = useAppSelector(headerTaxoSelector);
  const apiObject = useAppSelector(apiEndpointsSelector);
  const aos = useAppSelector(aosSelector);
  const getHost = () => {
    const win = useContext(WindowContext);
    const host = win?.location ? win.location.host : '';
    return host;
  };
  const host = getHost();
  const isStoresHost = host.includes('stores.');
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isHiding, setIsHiding] = useState(false);
  const [prevScrollY, setPrevScrollY] = useState(0);
  const progressiveHeaderEnabled = useAppSelector(
    getFeatureFlagByNameSelector('progressiveHeader'),
  );

  const myAccountDrawerEnabled = useAppSelector(getFeatureFlagByNameSelector('myAccountDrawer'));
  const stickyHeaderEnabled = useAppSelector(getFeatureFlagByNameSelector('stickyHeader'));
  const allCategoriesFlag = useAppSelector(getFeatureFlagByNameSelector('allCategories'));
  const l0LinksFlag = useAppSelector(getFeatureFlagByNameSelector('consolidatedNavL0Links'));
  const sameDayDeliveryEnabled = useAppSelector(
    getFeatureFlagByNameSelector('sameDayDeliveryEnabled'),
  );
  const trendingSearchInConsolidatedFlag = useAppSelector(
    getFeatureFlagByNameSelector('trendingSearchInConsolidatedNav'),
  );
  const headerLogoNetStorageEnabled = useAppSelector(
    getFeatureFlagByNameSelector('headerLogoNetStorageEnabled'),
  );

  const { thumbnailApi } = apiObject;
  const progressiveTaxoOnMobileEnabled = useAppSelector(
    getFeatureFlagByNameSelector('mobileOnlyProgressiveTaxo'),
  );

  const checkConsolidatedNavEnabledFlag = (
    allCategories: boolean,
    l0Links: boolean,
    trendingSearchInConsolidated: boolean,
    trendingSearchDataPresent: boolean,
  ) => {
    const value =
      allCategories || l0Links || (trendingSearchInConsolidated && trendingSearchDataPresent);

    return value;
  };
  const consolidatedNavEnabledAndData = checkConsolidatedNavEnabledFlag(
    allCategoriesFlag,
    l0LinksFlag,
    trendingSearchInConsolidatedFlag,
    !!trendingSearchData,
  );

  const viewPortBreakpoint = useAppSelector(breakpointSelector);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (stickyHeaderEnabled && currentScrollY > 103) {
        const isScrollingUp = currentScrollY < prevScrollY;
        setIsSticky(isScrollingUp);
        setIsHiding(!isScrollingUp);
      } else {
        setIsSticky(false);
        setIsHiding(false);
      }

      setPrevScrollY(currentScrollY);
    };
    startTransition(() => {
      window.addEventListener('scroll', handleScroll);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prevScrollY, stickyHeaderEnabled]);

  // Analytics event: Fires only once per page load, when mobile or desktop header navigation is opened
  const viewTopNavAnalyticsEventHandler = () => {
    if (!hasTopNavViewEventBeenCalled) {
      const evt = new CustomEvent('TopNavView');
      document.dispatchEvent(evt);
      setHasTopNavViewEventBeenCalled(true);
    }
  };

  const allCategoriesAndDesktop =
    !['mobile', 'tablet'].includes(viewPortBreakpoint) && allCategoriesFlag;

  const getHeaderClass = ({
    isStickyTrue,
    isHidingTrue,
    consolidatedNavEnabledTrue,
    showTrendingSearchTrue,
  }: {
    isStickyTrue: boolean;
    isHidingTrue: boolean;
    consolidatedNavEnabledTrue: boolean;
    showTrendingSearchTrue: boolean;
  }): string => {
    return `main-header hmf-align-items-center hmf-justify-content-center hmf-py-s hmf-pl-xlg-m hmf-pr-xlg-xs${
      isStickyTrue ? ' sticky hmf-position-fixed' : ''
    }${isHidingTrue ? ' hide-header' : ''}${
      consolidatedNavEnabledTrue || showTrendingSearchTrue ? '' : ' header-border'
    }`;
  };

  const getLogoClass = () => {
    return allCategoriesAndDesktop ? 'hmf-pl-l hmf-pb-s hmf-pb-xlg-0' : '';
  };

  return (
    <div
      ref={headerRef}
      className={getHeaderClass({
        isStickyTrue: isSticky,
        isHidingTrue: isHiding,
        consolidatedNavEnabledTrue: consolidatedNavEnabledAndData,
        showTrendingSearchTrue: showTrendingSearch,
      })}>
      {taxoContent.cta && (
        <ErrorBoundaryWithLogger componentId="MainHeader-TopMessage">
          <TopMessage
            topMessageData={taxoContent.cta}
            storeIdentifierName={storeIdentifierName as StoreIdentifierName}
          />
        </ErrorBoundaryWithLogger>
      )}
      <div className="header-container hmf-m-auto hmf-display-flex hmf-align-items-center hmf-flex-row hmf-flex-wrap hmf-justify-content-between">
        <div className="logo-menu-container hmf-display-flex hmf-align-items-center hmf-justify-content-between hmf-pl-s hmf-pl-md-xxs hmf-pl-xlg-0">
          {!progressiveHeaderEnabled ? (
            <ErrorBoundaryWithLogger componentId="MainHeader-MobileMenuToggleButton">
              <MobileMenuToggleButton
                showMobileMenu={showMobileMenu}
                setShowMobileMenu={setShowMobileMenu}
                viewTopNavAnalyticsEventHandler={viewTopNavAnalyticsEventHandler}
                setShowProgressiveTaxo={() => switchState()}
                showProgressiveTaxo={progressiveTaxoOnMobileEnabled}
              />
            </ErrorBoundaryWithLogger>
          ) : (
            !allCategoriesAndDesktop && (
              <HamburgerMenu
                setShowDrawer={() => switchState()}
                viewPortBreakpoint={viewPortBreakpoint}
                viewTopNavAnalyticsEventHandler={viewTopNavAnalyticsEventHandler}
                progressiveHeaderEnabled={progressiveHeaderEnabled}
              />
            )
          )}
          {/* Placeholder div to center the logo */}
          <div className="logo-placeholder hmf-display-md-none" />
          {headerLogoNetStorageEnabled ? (
            /* New Logo Loading Architecture */
            <MainLogo className={getLogoClass()} />
          ) : (
            <HeaderLogo
              storeIdentifierName={storeIdentifierName}
              newDsgLogo
              className={getLogoClass()}
            />
          )}
        </div>
        <ErrorBoundaryWithLogger componentId="MainHeader-SearchBar">
          <SearchBar
            storeIdentifierName={storeIdentifierName as StoreIdentifierName}
            headerRef={headerRef}
            thumbnailApi={thumbnailApi}
          />
        </ErrorBoundaryWithLogger>
        {(storeIdentifierName === 'dsg' || storeIdentifierName === 'gg') && aos === false && (
          <ErrorBoundaryWithLogger componentId="MainHeader-MyStore" fallback={<MyStoreFallback />}>
            <MyStore viewPortBreakpoint={viewPortBreakpoint} />
          </ErrorBoundaryWithLogger>
        )}
        {sameDayDeliveryEnabled && (
          <ErrorBoundaryWithLogger componentId="MainHeader-SameDayDeliveryModal">
            <SameDayDelivery viewPortBreakpoint={viewPortBreakpoint} />
          </ErrorBoundaryWithLogger>
        )}
        <div className="account_cart hmf-display-flex hmf-align-items-center hmf-pr-s hmf-pr-md-xs">
          {!aos && !isStoresHost && (
            <ErrorBoundaryWithLogger componentId="MainHeader-MyAccount">
              <MyAccount
                isMyAccountDrawer={myAccountDrawerEnabled}
                setShowDrawer={() => myAccountDrawerSwitchState()}
                storeIdentifierName={storeIdentifierName}
              />
            </ErrorBoundaryWithLogger>
          )}
          <ErrorBoundaryWithLogger componentId="MainHeader-CartCount">
            <CartCount />
          </ErrorBoundaryWithLogger>
        </div>
        <ErrorBoundaryWithLogger componentId="MainHeader-SAYTModalMobile">
          <SAYTModalMobile
            storeIdentifierName={storeIdentifierName as StoreIdentifierName}
            thumbnailApi={thumbnailApi}
          />
        </ErrorBoundaryWithLogger>
      </div>
    </div>
  );
};

export default MainHeader;
